import { useEffect, useState } from 'react';
import Modal from 'components/Modal/Modal';
import { DATA_SET_PAGINATION_COUNT, DISCLOSURE_GROUP_CELLS } from 'constants/dataSets';
import { useGetRefinementDisclosuresQuery } from 'store/slices/materialAssessment/apis/materialAssessmentApi';

import RefinementDisclosuresTable from './components/RefinementDisclosuresTable/RefinementDisclosuresTable';

import type IStakeholderGroupsDisclosuresItem from 'store/slices/materialAssessment/interfaces/IStakeholderGroupsDisclosuresItem';
import type IRefinementDisclosuresModal from './interfaces/IRefinementDisclosuresModal';

import styles from './scss/RefinementDisclosuresModal.module.scss';

function RefinementDisclosuresModal({
  open,
  title,
  onClose,
  currentGroupId,
  recordId,
}: IRefinementDisclosuresModal) {
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<IStakeholderGroupsDisclosuresItem[]>([]);

  const { data: refinementDisclosuresData, refetch } = useGetRefinementDisclosuresQuery(
    {
      maId: recordId,
      offset,
      id: currentGroupId ?? 0,
    },
    {
      skip: currentGroupId === null || recordId === '' || !open,
      refetchOnMountOrArgChange: true,
    },
  );

  const hasMore = refinementDisclosuresData?.data?.meta?.hasNext ?? false;

  const loadMore = () => {
    if (refinementDisclosuresData?.data?.meta?.hasNext) {
      setOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  useEffect(() => {
    if (!open) {
      setOffset(0);
      setItems([]);
    } else if (currentGroupId !== null && recordId) {
      refetch();
    }
  }, [open, currentGroupId, recordId, refetch]);

  useEffect(() => {
    if (currentGroupId === null || !recordId) {
      setOffset(0);
      setItems([]);
      return;
    }
    setOffset(0);
    setItems([]);
  }, [currentGroupId, recordId]);

  useEffect(() => {
    if (!refinementDisclosuresData?.data?.items?.length) return;
    const newItems = refinementDisclosuresData.data.items;
    setItems((prev) => {
      if (offset === 0) return newItems;
      const existingIds = new Set(prev.map((item) => item.id));
      const appended = newItems.filter((item) => !existingIds.has(item.id));
      return appended.length > 0 ? [...prev, ...appended] : prev;
    });
  }, [refinementDisclosuresData, offset]);

  return (
    <Modal
      open={open}
      title={title || ''}
      onClose={onClose}
      className={styles.modalContainer}
      isButtonsVisible={false}
    >
      <div className={styles.root}>
        <RefinementDisclosuresTable
          headCells={DISCLOSURE_GROUP_CELLS}
          items={items}
          hasMore={hasMore}
          loadMore={loadMore}
        />
      </div>
    </Modal>
  );
}

export default RefinementDisclosuresModal;
